import React, { lazy, Suspense, useState, useMemo } from 'react'
import { ProvinceSelection, CommuneSelection } from '../../types/map.types'
import { SelectionSummary } from './SelectionSummary'
import { ProvinceListView } from './ProvinceListView'
import './ZonesSection.styles.css'

// Lazy load the map component since it includes Leaflet which is large
const BelgiumMap = lazy(() => import('./BelgiumMap').then(module => ({ default: module.BelgiumMap })))

interface ZonesSectionProps {
  selectedProvinces: ProvinceSelection
  selectedCommunes: CommuneSelection
  onChange: (provinces: ProvinceSelection, communes: CommuneSelection) => void
}

export const ZonesSection: React.FC<ZonesSectionProps> = ({ 
  selectedProvinces, 
  selectedCommunes, 
  onChange 
}) => {
  const [isMapLoading, setIsMapLoading] = useState(true)
  const [selectionState, setSelectionState] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map')
  
  const hasSelection = Object.keys(selectedProvinces).some(key => selectedProvinces[key])
  
  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle view mode with Tab key
      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault()
        setViewMode(prev => prev === 'map' ? 'list' : 'map')
      }
      // Clear selection with Escape key
      if (e.key === 'Escape' && hasSelection) {
        handleClearAll()
      }
      // Focus search with Ctrl/Cmd + F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        const searchInput = document.querySelector('.search-input') as HTMLInputElement
        searchInput?.focus()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasSelection])
  
  const handleProvinceChange = (provinceName: string, selected: boolean, communeSelections?: Record<string, boolean>) => {
    const updatedProvinces = {
      ...selectedProvinces,
      [provinceName]: selected
    }
    
    // If deselecting a province, remove its communes
    if (!selected) {
      const updatedCommunes = { ...selectedCommunes }
      delete updatedCommunes[provinceName]
      onChange(updatedProvinces, updatedCommunes)
    } else {
      // If selecting or updating a province, update communes
      const updatedCommunes = {
        ...selectedCommunes,
        [provinceName]: communeSelections || {}
      }
      onChange(updatedProvinces, updatedCommunes)
    }
  }
  
  const handleClearAll = () => {
    onChange({}, {})
  }
  
  const handleProvinceRemove = (provinceName: string) => {
    const updatedProvinces = { ...selectedProvinces }
    delete updatedProvinces[provinceName]
    
    const updatedCommunes = { ...selectedCommunes }
    delete updatedCommunes[provinceName]
    
    onChange(updatedProvinces, updatedCommunes)
  }
  
  // Quick selection presets
  const handleQuickSelect = (preset: string) => {
    switch (preset) {
      case 'wallonia':
        onChange({
          'Hainaut': true,
          'Namur': true,
          'Liège': true,
          'Luxembourg': true,
          'Brabant Wallon': true
        }, {})
        break
      case 'flanders':
        onChange({
          'Anvers': true,
          'Limbourg': true,
          'Flandre Orientale': true,
          'Flandre Occidentale': true,
          'Brabant Flamand': true
        }, {})
        break
      case 'brussels':
        onChange({
          'Bruxelles': true
        }, {})
        break
      case 'major-cities': {
        const majorCities = {
          'Bruxelles': { 'Bruxelles': true },
          'Anvers': { 'Anvers': true },
          'Liège': { 'Liège': true },
          'Namur': { 'Namur': true }
        }
        onChange(
          Object.keys(majorCities).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
          majorCities
        )
        break
      }
    }
  }
  
  // Calculate statistics
  const statistics = useMemo(() => {
    const provinceCount = Object.values(selectedProvinces).filter(Boolean).length
    const communeCount = Object.values(selectedCommunes).reduce((total, communes) => {
      return total + Object.values(communes).filter(Boolean).length
    }, 0)
    return { provinceCount, communeCount }
  }, [selectedProvinces, selectedCommunes])
  
  return (
    <div className="zones-section">
      <div className="section-header">
        <div>
          <h2 className="section-title">Zones d'intervention</h2>
          <p className="section-description">
            Sélectionnez les provinces et communes où vous souhaitez intervenir
          </p>
        </div>
        <div className="header-actions">
          {hasSelection && (
            <span className="selection-stats">
              {statistics.provinceCount} province{statistics.provinceCount > 1 ? 's' : ''}, 
              {statistics.communeCount} commune{statistics.communeCount > 1 ? 's' : ''}
            </span>
          )}
          {hasSelection && (
            <button className="clear-btn" onClick={handleClearAll}>
              Effacer tout
            </button>
          )}
        </div>
      </div>
      
      <div className="zones-toolbar">
        <div className="search-container">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Rechercher une province ou commune..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="view-toggle">
          <button 
            className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
            onClick={() => setViewMode('map')}
            title="Vue carte"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 1v14h14V1H1zm1 1h5v5H2V2zm6 0h6v5H8V2zM2 8h5v6H2V8zm6 0h6v6H8V8z"/>
            </svg>
          </button>
          <button 
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="Vue liste"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="2" width="14" height="2" rx="1"/>
              <rect x="1" y="7" width="14" height="2" rx="1"/>
              <rect x="1" y="12" width="14" height="2" rx="1"/>
            </svg>
          </button>
        </div>
        
        <div className="quick-select">
          <span className="quick-select-label">Sélection rapide:</span>
          <button className="preset-btn" onClick={() => handleQuickSelect('wallonia')}>Wallonie</button>
          <button className="preset-btn" onClick={() => handleQuickSelect('flanders')}>Flandre</button>
          <button className="preset-btn" onClick={() => handleQuickSelect('brussels')}>Bruxelles</button>
          <button className="preset-btn" onClick={() => handleQuickSelect('major-cities')}>Grandes villes</button>
        </div>
      </div>
      
      <div className="zones-content">
        {viewMode === 'map' ? (
          <div className="map-container">
          <Suspense fallback={
            <div className="map-loading-placeholder">
              <div className="loading-spinner"></div>
              <p>Chargement de la carte...</p>
            </div>
          }>
            <BelgiumMap
              selectedProvinces={selectedProvinces}
              selectedCommunes={selectedCommunes}
              onProvinceChange={handleProvinceChange}
              onMapLoadingChange={setIsMapLoading}
              onSelectionStateChange={setSelectionState}
              searchQuery={searchQuery}
            />
          </Suspense>
          </div>
        ) : (
          <div className="list-view-container">
            <ProvinceListView
              selectedProvinces={selectedProvinces}
              selectedCommunes={selectedCommunes}
              onProvinceChange={handleProvinceChange}
              searchQuery={searchQuery}
            />
          </div>
        )}
        
        <div className="summary-container">
          {selectionState ? (
            <div className="map-controls-section">
              <div className="controls-content">
                <span className="controls-label">
                  Sélection des communes de {selectionState.selectedProvince}
                </span>
                <div className="controls-info">
                  {(() => {
                    const communes = selectionState.provinceCommunes[selectionState.selectedProvince] || {}
                    const selectedCount = Object.values(communes).filter(Boolean).length
                    const totalCount = Object.keys(communes).length
                    return (
                      <span className="commune-count-info">
                        {selectedCount} / {totalCount} communes sélectionnées
                      </span>
                    )
                  })()}
                </div>
                <div className="controls-buttons">
                  <button 
                    className="btn-select-all"
                    onClick={() => selectionState.handleToggleAllCommunes(true)}
                    disabled={Object.values(selectionState.provinceCommunes[selectionState.selectedProvince] || {}).every(v => v)}
                  >
                    Tout sélectionner
                  </button>
                  <button 
                    className="btn-deselect-all"
                    onClick={() => selectionState.handleToggleAllCommunes(false)}
                    disabled={!Object.values(selectionState.provinceCommunes[selectionState.selectedProvince] || {}).some(v => v)}
                  >
                    Tout désélectionner
                  </button>
                </div>
                <div className="controls-buttons">
                  <button 
                    className="btn-validate"
                    onClick={selectionState.handleValidateProvince}
                    disabled={!Object.values(selectionState.provinceCommunes[selectionState.selectedProvince] || {}).some(v => v)}
                  >
                    ✓ Valider
                  </button>
                  <button 
                    className="btn-cancel"
                    onClick={selectionState.handleCancelProvince}
                  >
                    ✗ Annuler
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <SelectionSummary
                selectedProvinces={selectedProvinces}
                selectedCommunes={selectedCommunes}
                isMapLoading={isMapLoading}
                onProvinceRemove={handleProvinceRemove}
              />
              
              <div className="zones-info">
                <h4 className="info-title">Comment utiliser la carte</h4>
                <ul className="info-list">
                  <li>Cliquez sur une province pour la sélectionner</li>
                  <li>Une fois sélectionnée, les communes apparaissent</li>
                  <li>Cliquez sur les communes pour affiner votre sélection</li>
                  <li>Utilisez la molette pour zoomer</li>
                </ul>
                <h4 className="info-title" style={{ marginTop: '16px' }}>Raccourcis clavier</h4>
                <ul className="info-list">
                  <li><kbd>Shift</kbd> + <kbd>Tab</kbd> : Basculer entre carte et liste</li>
                  <li><kbd>Ctrl</kbd> + <kbd>F</kbd> : Rechercher</li>
                  <li><kbd>Esc</kbd> : Effacer la sélection</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}